import { z } from "zod";

const riskIdentityValueSchema = z.union([z.string(), z.number(), z.boolean()]).nullish();
type RiskIdentityValue = z.infer<typeof riskIdentityValueSchema>;

interface RiskCompanyIdentity {
  company?: RiskIdentityValue;
  company_label?: RiskIdentityValue;
  company_id?: RiskIdentityValue;
  company_id_label?: RiskIdentityValue;
}

interface RiskContextResetPolicyInput {
  previousRiskOrigin: string | undefined;
  riskOrigin: string;
  riskOriginChangedByUser: boolean;
  previousCreateCompany: boolean | null | undefined;
  createCompany: boolean | null;
  createCompanyChangedByUser: boolean;
}

const normalizeIdentityValue = (value: RiskIdentityValue): string => {
  const parsed = riskIdentityValueSchema.safeParse(value);
  return parsed.success && parsed.data !== null && parsed.data !== undefined
    ? String(parsed.data).trim()
    : "";
};

const hasHydratedValueChanged = <T,>(previous: T | undefined, current: T): boolean =>
  previous !== undefined && previous !== current;

export function normalizeRiskCompanyIdentity(identity: RiskCompanyIdentity) {
  const canonicalCompanyId = normalizeIdentityValue(identity.company_id);
  const canonicalCompanyLabel = normalizeIdentityValue(identity.company_id_label);

  return {
    companyId: canonicalCompanyId || normalizeIdentityValue(identity.company),
    companyLabel: canonicalCompanyLabel || normalizeIdentityValue(identity.company_label),
  };
}

export function getRiskContextResetPolicy({
  previousRiskOrigin,
  riskOrigin,
  riskOriginChangedByUser,
  previousCreateCompany,
  createCompany,
  createCompanyChangedByUser,
}: RiskContextResetPolicyInput) {
  return {
    resetForRiskOrigin:
      riskOriginChangedByUser && hasHydratedValueChanged(previousRiskOrigin, riskOrigin),
    resetForCreateCompany:
      createCompanyChangedByUser &&
      hasHydratedValueChanged(previousCreateCompany, createCompany),
  };
}
